import type { ComponentPropsWithRef, ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface SplitProps extends ComponentPropsWithRef<"div"> {
  /** Largeur de la barre latérale ("xs" 240px, "sm" 280px, "md" 320px, "lg" 380px). Par défaut "sm". */
  sidebarWidth?: "xs" | "sm" | "md" | "lg";
  /** Côté de la barre latérale ("left" ou "right"). Par défaut "left". */
  sidebarPosition?: "left" | "right";
  /** Élément de la barre latérale. */
  sidebar: ReactNode;
  /** Espacement entre la barre latérale et le contenu principal. Par défaut 6. */
  gap?: 4 | 6 | 8;
  /** Rend la barre latérale collante au défilement (sticky). */
  sticky?: boolean;
  /** Décalage depuis le haut si sticky (ex: "top-16"). Par défaut "top-16". */
  stickyTop?: string;
  children?: ReactNode;
}

const SIDEBAR_WIDTHS = {
  xs: "w-full lg:w-60", // 240px
  sm: "w-full lg:w-70", // 280px
  md: "w-full lg:w-80", // 320px
  lg: "w-full lg:w-96", // 384px
} as const;

const GAP_CLASSES = {
  4: "gap-4",
  6: "gap-6",
  8: "gap-8",
} as const;

/**
 * Composant de disposition 2 colonnes / SidebarLayout réactif SOTA.
 * S'empile proprement sur mobile et se sépare en colonne latérale fixe sur desktop.
 */
export function Split({
  className,
  sidebarWidth = "sm",
  sidebarPosition = "left",
  sidebar,
  gap = 6,
  sticky = false,
  stickyTop = "top-16",
  children,
  ...props
}: SplitProps) {
  const sidebarElement = (
    <aside
      className={cn(
        "shrink-0",
        SIDEBAR_WIDTHS[sidebarWidth],
        sticky && cn("lg:sticky lg:h-[calc(100vh-4rem)] lg:overflow-y-auto", stickyTop),
      )}
    >
      {sidebar}
    </aside>
  );

  return (
    <div
      className={cn(
        "flex w-full flex-col lg:flex-row",
        GAP_CLASSES[gap],
        sidebarPosition === "right" && "lg:flex-row-reverse",
        className,
      )}
      {...props}
    >
      {sidebarElement}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function SidebarLayout(props: SplitProps) {
  return <Split {...props} />;
}
